import React, { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { API_BASE_URL } from "../config/api";

const translations = {
  tr: {
    title: "HİZMETLERİMİZ",
    description: "Fransa’daki hukuki süreçlerde sunduğumuz profesyonel ve güvenilir danışmanlık hizmetleri.",
    readMore: "Detaylı Bilgi",
    loading: "Yükleniyor...",
    error: "Hizmetler yüklenirken bir hata oluştu.",
    noServices: "Henüz hizmet bulunmamaktadır."
  },
  en: {
    title: "OUR SERVICES",
    description: "Professional and trusted legal consultancy services we provide in France.",
    readMore: "Read More",
    loading: "Loading...",
    error: "Error loading services.",
    noServices: "No services found."
  },
  fr: {
    title: "NOS SERVICES",
    description: "Services de conseil juridique professionnels et fiables que nous proposons en France.",
    readMore: "Lire la Suite",
    loading: "Chargement...",
    error: "Erreur lors du chargement des services.",
    noServices: "Aucun service trouvé."
  }
};

const responsiveStyles = `
  /* Tüm ekranlar: Bootstrap row override */
  .services-grid.row {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
  }

  /* Desktop: equal height cards */
  @media (min-width: 992px) {
    .services-grid .service-col {
      display: flex !important;
    }
    .services-grid .service-col .service-card-inner {
      flex: 1;
    }
  }

  /* Tablet & Mobile */
  @media (max-width: 991px) {
    .services-grid.row {
      flex-direction: column !important;
      align-items: center !important;
    }
    .services-grid .service-col {
      width: 90% !important;
      max-width: 500px !important;
      float: none !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

export const ServicesPage = ({ lang, isDarkMode, onServiceClick }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentLang = lang ? lang.toUpperCase() : 'TR';
  const t = translations[lang?.toLowerCase()] || translations.tr;

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/practicearea`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Services loaded:', data.length);
      setServices(data);

    } catch (err) {
      console.error('Fetch services error:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (serviceId) => {
    if (onServiceClick) {
      onServiceClick(serviceId);
    }
  };

  const getLocalizedField = (service, field) => {
    const langField = `${field}${currentLang}`;
    const fallbackField = `${field}TR`;
    return service[langField] || service[fallbackField] || '';
  };

  const cardStyle = {
    cursor: 'pointer',
    padding: '30px',
    borderRadius: '12px',
    background: isDarkMode ? '#1a1a2e' : '#ffffff',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e0e0',
    boxShadow: isDarkMode
      ? '0 5px 15px rgba(0,0,0,0.3)'
      : '0 5px 15px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    textAlign: 'center'
  };

  return (
    <div id="services" className="text-center" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
      <style>{responsiveStyles}</style>

      <SEO
        title={t.title}
        description={t.description}
        keywords="hukuk hizmetleri, legal services, avukat, danışmanlık"
        url="/services"
        lang={currentLang.toLowerCase()}
      />
      <div className="container">
        <div className="section-title" style={{ marginTop: '60px', marginBottom: '60px' }}>
          <h2>{t.title}</h2>
          <p>{t.description}</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: '#1e7a46' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{t.loading}</h3>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{error}</h3>
            <button
              onClick={fetchServices}
              style={{
                marginTop: '20px',
                padding: '12px 30px',
                background: '#1e7a46',
                color: '#fff',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              <i className="fa fa-refresh"></i> Tekrar Dene
            </button>
          </div>
        )}

        {/* No Services State */}
        {!loading && !error && services.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fa fa-briefcase" style={{ fontSize: '48px', color: '#999' }}></i>
            <h3 style={{ marginTop: '20px', color: '#666' }}>{t.noServices}</h3>
          </div>
        )}

        {/* Services Grid */}
        {!loading && !error && services.length > 0 && (
          <div className="row services-grid">
            {services.map((service) => {
              const title = getLocalizedField(service, 'title');
              const description = getLocalizedField(service, 'description');

              return (
                <div key={service.id} className="col-md-4 service-col" style={{ marginBottom: '30px' }}>
                  <div
                    className="service-card-inner"
                    onClick={() => handleServiceClick(service.id)}
                    style={cardStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
                      e.currentTarget.style.borderColor = '#1e7a46';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isDarkMode
                        ? '0 5px 15px rgba(0,0,0,0.3)'
                        : '0 5px 15px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = isDarkMode
                        ? 'rgba(255,255,255,0.1)'
                        : '#e0e0e0';
                    }}
                  >
                    {service.icon && (
                      <i
                        className={`fa ${service.icon}`}
                        style={{
                          fontSize: '48px',
                          color: '#1e7a46',
                          marginBottom: '20px',
                          display: 'block'
                        }}
                      ></i>
                    )}
                    <h3 style={{ fontSize: '22px', marginBottom: '15px', fontWeight: '600' }}>
                      {title}
                    </h3>
                    <p style={{
                      color: isDarkMode ? '#bbb' : '#666',
                      fontSize: '15px',
                      marginBottom: '20px'
                    }}>
                      {description}
                    </p>
                    <span style={{ color: '#1e7a46', fontWeight: '600', fontSize: '14px' }}>
                      {t.readMore} <i className="fa fa-arrow-right"></i>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;